import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { useToast } from '@chakra-ui/react'
import { api } from '../../../../services/api'
import { useUser } from '../../../../hooks/useUser'

export function useRecebimentoTroca() {
  const { id: paramNumNota } = useParams()
  const [numNota, setNumNota] = useState(paramNumNota || '')
  const [selectedSequence, setSelectedSequence] = useState('')
  const toast = useToast()
  const { codigo_usuario } = useUser()

  const {
    data: pedidoData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery(
    ['pedido-recebimento-troca', numNota],
    async () => {
      if (!numNota) return null
      const response = await api.get(`/ordem-producao/listar-status/${numNota}`)
      return response.data
    },
    {
      enabled: true,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutos
      onError: (err) => {
        toast({
          title: 'Erro ao carregar dados',
          description: err?.response?.data?.message || 'Não foi possível carregar os dados do pedido',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      },
    }
  )

  const {
    data: itemData,
    isLoading: isItemLoading,
    refetch: refetchItem,
  } = useQuery(
    ['item-troca', numNota, selectedSequence],
    async () => {
      if (!numNota || !selectedSequence) return null
      const response = await api.get(`/ordem-producao/listar-status/${numNota}/${selectedSequence}`)
      return response.data
    },
    {
      enabled: Boolean(numNota && selectedSequence),
      retry: 1,
      staleTime: 1000 * 60 * 5,
    }
  )

  const receberTrocaMutation = useMutation(
    async (data) => {
      return await api.post('/ordem-producao/receber-troca', data)
    },
    {
      onSuccess: () => {
        toast({
          title: 'Troca recebida com sucesso',
          description: 'O recebimento da troca foi registrado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        refetch()
        setSelectedSequence('')
      },
      onError: (error) => {
        toast({
          title: 'Erro ao receber troca',
          description: error?.response?.data?.message || 'Ocorreu um erro ao processar o recebimento da troca',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      },
    }
  )

  const confirmarRecebimento = async () => {
    if (!selectedSequence) {
      toast({
        title: 'Selecione um item',
        description: 'Por favor, selecione um item para confirmar o recebimento',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const payload = {
      nunota: numNota,
      sequencia: selectedSequence,
      codusu: codigo_usuario,
    }

    await receberTrocaMutation.mutateAsync(payload)
  }

  const handleSearch = () => {
    if (!numNota) {
      toast({
        title: 'Número da nota é obrigatório',
        description: 'Por favor, informe o número da nota para pesquisar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    refetch()
  }

  const getItensComTrocasPendentes = () => {
    if (!pedidoData || !pedidoData.itens) return []

    return pedidoData.itens.filter((item) => item.status === 'Troca Solicitada' || (item.data_usuario_troca && !item.data_final_troca))
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Não informado'
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch (e) {
      return dateStr
    }
  }

  return {
    numNota,
    setNumNota,
    selectedSequence,
    setSelectedSequence,
    pedidoData,
    itemData,
    isLoading,
    isItemLoading,
    isError,
    error,
    isFetching,
    loading: receberTrocaMutation.isLoading,
    handleSearch,
    confirmarRecebimento,
    getItensComTrocasPendentes,
    formatDate,
  }
}
